// File: /home/ubuntu/avaai_dashboard/frontend/src/pages/Support.tsx
import React, { useContext, useEffect, useState } from "react";
import { getAuthToken } from "../utils/auth";
import { Link, useNavigate } from "react-router-dom";
import { ThemeContext } from "../context/ThemeContext";

type SupportCategory =
  | "general"
  | "billing"
  | "technical"
  | "account"
  | "feature"
  | "other";

type SavedUser = {
  name?: string;
  display_name?: string;
  full_name?: string;
  username?: string;
  email?: string;
};

function getStoredUser(): SavedUser {
  const keys = ["ava_user", "user", "ava_profile", "profile", "user_profile"];

  for (const key of keys) {
    try {
      const raw = localStorage.getItem(key) || sessionStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") return parsed;
    } catch {
      // ignore malformed storage
    }
  }

  return {};
}

function getStoredName(user: SavedUser): string {
  return user.display_name || user.name || user.full_name || user.username || "";
}

function getStoredEmail(user: SavedUser): string {
  return user.email || "";
}

export default function Support() {
  const navigate = useNavigate();
  const { themeColors } = useContext(ThemeContext);
  const storedUser = getStoredUser();

  const [name, setName] = useState(getStoredName(storedUser));
  const [email, setEmail] = useState(getStoredEmail(storedUser));
  const [category, setCategory] = useState<SupportCategory>("general");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [includeAccountContext, setIncludeAccountContext] = useState(true);

  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [errorText, setErrorText] = useState("");

  const token = getAuthToken() || "";

  const [isMobileSupport, setIsMobileSupport] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth <= 760;
  });

  useEffect(() => {
    const syncMobileSupport = () => setIsMobileSupport(window.innerWidth <= 760);
    syncMobileSupport();
    window.addEventListener("resize", syncMobileSupport);
    return () => window.removeEventListener("resize", syncMobileSupport);
  }, []);

  useEffect(() => {
    document.title = "AVAAIO™ Support";
  }, []);

  const apiBase = (
    (import.meta as any).env?.VITE_API_URL ||
    (import.meta as any).env?.VITE_API_BASE ||
    "https://api.avaaio.com"
  ).replace(/\/$/, "");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorText("");
    setSubmitted(false);

    if (!name.trim() || !email.trim() || !subject.trim() || !message.trim()) {
      setErrorText("Please complete all required fields.");
      return;
    }

    try {
      setSending(true);

      const payload = {
        name: name.trim(),
        email: email.trim(),
        category,
        subject: subject.trim(),
        message: message.trim(),
        include_account_context: includeAccountContext,
        session_context: includeAccountContext
          ? {
              has_token: !!token,
              user_agent: navigator.userAgent,
              page: window.location.href,
            }
          : null,
      };

      const res = await fetch(`${apiBase}/support`, { credentials: "include",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data?.ok) {
        throw new Error(data?.message || "Failed to send support request.");
      }

      setSubmitted(true);
      setSubject("");
      setMessage("");
    } catch (err: any) {
      setErrorText(err?.message || "Unable to send support request right now.");
    } finally {
      setSending(false);
    }
  };

  const shellStyle: React.CSSProperties = {
    minHeight: "100vh",
    background: themeColors.bg,
    color: themeColors.text,
    fontFamily: "Orbitron, sans-serif",
    padding: isMobileSupport ? "18px 12px 110px" : "28px 16px 90px",
    overflowX: "hidden",
    boxSizing: "border-box",
  };

  const pageStyle: React.CSSProperties = {
    maxWidth: 1280,
    width: "100%",
    margin: "0 auto",
    minWidth: 0,
  };

  const topGrid: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: isMobileSupport ? "1fr" : "1.2fr 0.9fr",
    gap: isMobileSupport ? 12 : 16,
    alignItems: "stretch",
    marginBottom: 16,
  };

  const heroStyle: React.CSSProperties = {
    border: `1px solid ${themeColors.border}`,
    borderRadius: isMobileSupport ? 20 : 28,
    padding: isMobileSupport ? "18px 14px" : "24px 24px 22px",
    background: themeColors.panel,
    boxShadow: themeColors.glow,
    backdropFilter: "blur(10px)",
  };

  const sideStyle: React.CSSProperties = {
    border: `1px solid ${themeColors.border}`,
    borderRadius: isMobileSupport ? 20 : 28,
    padding: isMobileSupport ? "18px 14px" : "24px 24px 22px",
    background: themeColors.panel,
    boxShadow: themeColors.glow,
    backdropFilter: "blur(10px)",
  };

  const bodyGrid: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: isMobileSupport ? "1fr" : "1.1fr 0.9fr",
    gap: isMobileSupport ? 12 : 16,
    alignItems: "start",
  };

  const formCard: React.CSSProperties = {
    border: `1px solid ${themeColors.border}`,
    borderRadius: isMobileSupport ? 20 : 24,
    padding: isMobileSupport ? 16 : 22,
    background: themeColors.panel,
    boxShadow: themeColors.glow,
  };

  const infoCard: React.CSSProperties = {
    border: `1px solid ${themeColors.border}`,
    borderRadius: isMobileSupport ? 20 : 24,
    padding: isMobileSupport ? 16 : 22,
    background: themeColors.panel,
    boxShadow: themeColors.glow,
  };

  const titleStyle: React.CSSProperties = {
    margin: 0,
    fontSize: isMobileSupport ? "clamp(1.75rem, 9vw, 2.45rem)" : "clamp(2.2rem, 4vw, 4rem)",
    lineHeight: 1,
    color: themeColors.accent,
    letterSpacing: 1,
  };

  const eyebrowStyle: React.CSSProperties = {
    color: themeColors.accent2,
    fontSize: 12,
    letterSpacing: 2,
    textTransform: "uppercase",
    opacity: 0.9,
    marginBottom: 10,
  };

  const subStyle: React.CSSProperties = {
    marginTop: 14,
    maxWidth: 760,
    fontSize: isMobileSupport ? 14 : 15,
    lineHeight: 1.6,
    color: themeColors.mutedText,
  };

  const badgeStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    marginTop: 16,
    padding: "8px 12px",
    borderRadius: 999,
    border: `1px solid ${themeColors.border}`,
    background: themeColors.buttonBg,
    color: themeColors.accent2,
    fontSize: 12,
  };

  const buttonRow: React.CSSProperties = {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    marginTop: 18,
  };

  const linkButton: React.CSSProperties = {
    textDecoration: "none",
    color: themeColors.text,
    background: themeColors.buttonBg,
    border: `1px solid ${themeColors.border}`,
    padding: "12px 16px",
    borderRadius: 14,
    fontWeight: 700,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: isMobileSupport ? "100%" : "auto",
    minWidth: 0,
    textAlign: "center",
  };

  const sectionTitle: React.CSSProperties = {
    margin: "0 0 16px 0",
    fontSize: 22,
    color: themeColors.accent2,
    letterSpacing: 1,
  };

  const fieldGrid: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: isMobileSupport ? "1fr" : "repeat(2, minmax(0, 1fr))",
    gap: isMobileSupport ? 12 : 14,
  };

  const fullWidth: React.CSSProperties = {
    gridColumn: "1 / -1",
  };

  const labelStyle: React.CSSProperties = {
    display: "block",
    marginBottom: 8,
    fontSize: 13,
    color: themeColors.mutedText,
    fontWeight: 700,
    letterSpacing: 0.4,
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "14px 14px",
    borderRadius: 14,
    border: `1px solid ${themeColors.border}`,
    background: themeColors.panel,
    color: themeColors.text,
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
    fontSize: 14,
  };

  const textareaStyle: React.CSSProperties = {
    ...inputStyle,
    minHeight: 180,
    resize: "vertical" as const,
  };

  const submitBtn: React.CSSProperties = {
    marginTop: 16,
    border: `1px solid ${themeColors.border}`,
    background: themeColors.buttonBg,
    color: themeColors.text,
    borderRadius: 14,
    padding: "13px 18px",
    fontWeight: 800,
    fontSize: 14,
    cursor: sending ? "not-allowed" : "pointer",
    fontFamily: "inherit",
    boxShadow: themeColors.glow,
    opacity: sending ? 0.7 : 1,
  };

  const mutedText: React.CSSProperties = {
    fontSize: 13,
    lineHeight: 1.55,
    color: themeColors.mutedText,
  };

  const statCard: React.CSSProperties = {
    border: "1px solid rgba(255,255,255,0.10)",
    background: "linear-gradient(180deg, rgba(8,18,32,0.82), rgba(3,8,16,0.68))",
    borderRadius: 18,
    padding: "16px 16px",
    boxShadow: "0 14px 34px rgba(0,0,0,0.22)",
    marginBottom: 12,
  };

  const statLabel: React.CSSProperties = {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 1,
    color: "rgba(234,252,255,0.60)",
    marginBottom: 6,
  };

  const statValue: React.CSSProperties = {
    fontSize: 15,
    fontWeight: 700,
    lineHeight: 1.35,
  };

  const successBox: React.CSSProperties = {
    marginTop: 16,
    padding: 14,
    borderRadius: 14,
    background: "rgba(0,255,170,0.08)",
    border: "1px solid rgba(0,255,170,0.22)",
    color: "#caffef",
    lineHeight: 1.55,
  };

  const errorBox: React.CSSProperties = {
    marginTop: 16,
    padding: 14,
    borderRadius: 14,
    background: "rgba(255,80,80,0.08)",
    border: "1px solid rgba(255,80,80,0.24)",
    color: "#ffd6d6",
    lineHeight: 1.55,
  };

  return (
    <div style={shellStyle}>
      <div style={pageStyle}>
        <div style={topGrid}>
          <section style={heroStyle}>
            <div style={eyebrowStyle}>AVAAIO™ Help Center</div>
            <h1 style={titleStyle}>HELP / SUPPORT</h1>

            <div style={subStyle}>
              Get help with your AVAAIO™ account, billing, login access, subscriptions,
              technical issues, feature requests, and platform guidance. Ava Support is
              here to help keep your experience smooth, secure, and connected.
            </div>

            <div style={{ marginTop: 12, fontSize: 14, color: "#7cefff" }}>
              Priority Support Email: <strong>support@avaaio.com</strong>
            </div>

            <div style={badgeStyle}>
              <span>●</span>
              <span>{token ? "Secure signed-in support session" : "Guest support session"}</span>
            </div>

            
          </section>

          <aside style={sideStyle}>
            <h2 style={sectionTitle}>Support Overview</h2>
<div style={statCard}>
              <div style={statLabel}>Support Channel</div>
              <div style={statValue}>AVAAIO Support Desk</div>
            </div>

            <div style={statCard}>
              <div style={statLabel}>Recommended Support Email</div>
              <div style={statValue}>support@avaaio.com</div>
            </div>

            <div style={statCard}>
              <div style={statLabel}>User State</div>
              <div style={statValue}>{token ? "Authenticated user" : "Guest user"}</div>
            </div>

            <div style={mutedText}>
              You can also contact Ava Support directly at:
              <br />
              <br />
              <span style={{ color: "#19e6ff", fontWeight: 700 }}>
                support@avaaio.com
              </span>
              <br />
              <br />
              Every request is reviewed carefully. Include details like screenshots,
              account email, device, browser, and steps to reproduce when possible.
            </div>
          </aside>
        </div>

        <div style={bodyGrid}>
          <section style={formCard}>
            <h2 style={sectionTitle}>Contact Support</h2>
            <div style={{ ...mutedText, marginBottom: 18 }}>
              Send a secure request to the AVAAIO™ support team. The more detail you include,
              the faster we can understand and route your issue.
            </div>

            <form onSubmit={handleSubmit}>
              <div style={fieldGrid}>
                <div>
                  <label style={labelStyle}>Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    style={inputStyle}
                    required
                  />
                </div>

                <div>
                  <label style={labelStyle}>Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email"
                    style={inputStyle}
                    required
                  />
                </div>

                <div>
                  <label style={labelStyle}>Category</label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value as SupportCategory)}
                    style={inputStyle}
                  >
                    <option value="general">General</option>
                    <option value="billing">Billing</option>
                    <option value="technical">Technical</option>
                    <option value="account">Account</option>
                    <option value="feature">Feature Request</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label style={labelStyle}>Subject</label>
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="How can we help?"
                    style={inputStyle}
                    required
                  />
                </div>

                <div style={fullWidth}>
                  <label style={labelStyle}>Message</label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Describe what happened, what you expected, and any steps we can use to reproduce the issue..."
                    style={textareaStyle}
                    required
                  />
                </div>

                <div style={fullWidth}>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      fontSize: 13,
                      color: "rgba(234,252,255,0.82)",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={includeAccountContext}
                      onChange={(e) => setIncludeAccountContext(e.target.checked)}
                    />
                    Include account/session context with this support request
                  </label>
                </div>
              </div>

              <button type="submit" style={submitBtn} disabled={sending}>
                {sending ? "Sending Support Request..." : "Send Support Request"}
              </button>
            </form>

            {errorText ? <div style={errorBox}>{errorText}</div> : null}

            {submitted ? (
              <div style={successBox}>
                Your support request has been sent successfully.
                <br />
                <br />
                A copy has been routed to:
                <br />
                <span style={{ color: "#19e6ff", fontWeight: 700 }}>
                  support@avaaio.com
                </span>
                <br />
                <br />
                Our support team will review your request and respond as soon as possible.
              </div>
            ) : null}
          </section>

          <section style={infoCard}>
            <h2 style={sectionTitle}>Support Categories</h2>
            <div style={{ ...mutedText, marginBottom: 14 }}>
              Choose the category that best matches your request so support can route it properly.
            </div>

            <div style={statCard}>
              <div style={statLabel}>Account Help</div>
              <div style={statValue}>Login issues, password reset, verification, account recovery</div>
            </div>

            <div style={statCard}>
              <div style={statLabel}>Billing</div>
              <div style={statValue}>Memberships, subscriptions, renewals, cancellations, Apple billing</div>
            </div>

            <div style={statCard}>
              <div style={statLabel}>Technical</div>
              
            </div>

            <div style={statCard}>
              <div style={statLabel}>Feature Requests</div>
              <div style={statValue}>Ideas for Ava, education tools, games, workflow help, and new features</div>
            </div>

            <div style={mutedText}>
              Users can reach support directly at{" "}
              <span style={{ color: "#19e6ff", fontWeight: 700 }}>
                support@avaaio.com
              </span>
              . Please allow some time for our support team to review incoming
              requests. We appreciate your patience.
            </div>
          
</section>
        </div>
      </div>
    </div>
  );
}
